<template>
	<div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
		<div class="max-w-5xl mx-auto">
			<TransitionRoot as="template" :show="open">
				<Dialog
					as="div"
					static
					class="fixed z-10 inset-0 overflow-y-auto"
					:open="open"
				>
					<div
						class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
					>
						<TransitionChild
							as="template"
							enter="ease-out duration-300"
							enter-from="opacity-0"
							enter-to="opacity-100"
							leave="ease-in duration-200"
							leave-from="opacity-100"
							leave-to="opacity-0"
						>
							<DialogOverlay
								class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
							/>
						</TransitionChild>

						<!-- This element is to trick the browser into centering the modal contents. -->
						<span
							class="hidden sm:inline-block sm:align-middle sm:h-screen"
							aria-hidden="true"
							>&#8203;</span
						>
						<TransitionChild
							as="template"
							enter="ease-out duration-300"
							enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							enter-to="opacity-100 translate-y-0 sm:scale-100"
							leave="ease-in duration-200"
							leave-from="opacity-100 translate-y-0 sm:scale-100"
							leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						>
							<div
								tabindex="0"
								class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-sm sm:w-full sm:p-6"
							>
								<div>
									<div
										class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100"
									>
										<RefreshIcon
											class="animate-spin h-6 w-6 text-green-600"
											aria-hidden="true"
										/>
									</div>
									<div class="mt-3 text-center sm:mt-5">
										<DialogTitle
											as="h3"
											class="text-lg leading-6 font-medium text-gray-900"
										>
											Ihre Bestellung wird bearbeitet
										</DialogTitle>
										<div class="mt-2">
											<p class="text-sm text-gray-500">
												Bitte aktualisieren Sie diese Seite nicht.
											</p>
										</div>
									</div>
								</div>
							</div>
						</TransitionChild>
					</div>
				</Dialog>
			</TransitionRoot>
			<div
				class="shadow rounded-lg divide-gray-200"
				:class="{ 'bg-brand': currentStep.step === 5 }"
			>
				<div class="px-2 py-2 sm:px-6 sm:py-6">
					<form-header-steps v-if="currentStep.step !== 5" :steps="steps" />
					<transition name="bounce" mode="out-in">
						<div
							:key="currentStep.name"
							class="mt-6 text-center max-w-xl mx-auto"
							:class="{'hidden': currentStep.step <= 2}"
						>
							<h3
								class="text-2xl leading-6 font-bold text-brand"
								:class="{ 'text-white': currentStep.step === 5, 'hidden': currentStep.step <= 2 }"
							>
								{{ currentStep.name }}
							</h3>
							<p
								v-if="currentStep.step === 3"
								class="mt-1 text-sm text-gray-500"
							>
								Bitte geben Sie Ihre Rechnungs- und Bezahldaten ein
							</p>
							<p
								v-if="currentStep.step === 4"
								class="mt-1 text-sm text-gray-500"
							>
								Bitte überprüfen Sie die unten aufgeführten Details und klicken
								"Jetzt kostenpflichtig zu obigem Preis bestellen", wenn alle
								Informationen korrekt sind.
							</p>
						</div>
					</transition>
				</div>
				<div class="px-4 py-5 sm:px-6 sm:py-12 md:px-16">
					<transition :name="transitionName" mode="out-in">
						<component
							:is="currentStep.component"
							@changed="updateFormData"
						></component>
					</transition>
				</div>
				<div class="bg-brand rounded-b-lg px-4 py-4 sm:px-6">
					<form-footer
						:is-first-step="isFirstStep"
						:current-step="currentStep"
						@next="goNext"
						@previous="goPrevious"
					/>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import {
	Dialog,
	DialogOverlay,
	DialogTitle,
	TransitionChild,
	TransitionRoot,
} from '@headlessui/vue'
import { RefreshIcon } from '@heroicons/vue/outline'
import FormHeaderSteps from '@/components/Form/FormHeaderSteps.vue'
import FormFooter from '@/components/Form/FormFooter.vue'
import StepOne from '@/components/Steps/StepOne.vue'
import StepTwo from '@/components/Steps/StepTwo.vue'
import StepThree from '@/components/Steps/StepThree.vue'
import StepFour from '@/components/Steps/StepFour.vue'
import StepFive from '@/components/Steps/StepFive.vue'
import { ref, reactive, computed, watchEffect } from 'vue'
import { useStore } from 'vuex'
import axios from 'axios'

// Change the API URL from the .env file
const api_url = import.meta.env.VITE_API_URL

export default {
	components: {
		FormHeaderSteps,
		FormFooter,

		StepOne,
		StepTwo,
		StepThree,
		StepFour,
		StepFive,

		Dialog,
		DialogOverlay,
		DialogTitle,
		TransitionChild,
		TransitionRoot,
		RefreshIcon,
	},
	setup() {
		const store = useStore()
		const currentStep = computed(() => store.state.steps.currentStep)

		const formDataValues = computed(() => {
			return { ...store.state.steps.formData }
		})

		const steps = computed(() => {
			if (currentStep.value.step <= 4) {
				return store.state.steps.all.slice(0, 4)
			}
			return [
				{
					id: '01',
					name: 'Objektdaten',
					href: '#',
					status: 'complete',
					step: 1,
				},
				...store.state.steps.all.slice(4, 6),
			]
		})
		const transitionName = computed(() => store.state.steps.transitionName)
		const isFirstStep = computed(() => {
			return currentStep.value.step === 1
		})

		const mapFormValues = () => {
			let output = {}

			Object.keys(formDataValues.value).map(function (item) {
				if (
					formDataValues.value[item] !== undefined &&
					formDataValues.value[item] !== null &&
					formDataValues.value[item]['value'] !== null &&
					formDataValues.value[item]['value'] !== undefined
				) {
					output[item] = formDataValues.value[item]['value']
				} else if (
					formDataValues.value[item] === undefined ||
					formDataValues.value[item] === null
				) {
					output[item] = null
				} else if (Array.isArray(formDataValues.value[item])) {
					const arr = []
					Object.keys(formDataValues.value[item]).map((key) => {
						let obj = {}
						Object.keys(formDataValues.value[item][key]).map((i) => {
							obj[i] = formDataValues.value[item][key][i]
						})
						arr.push(obj)
					})
					output[item] = arr
				} else if (item === 'Rechnung') {
					let result = {}
					Object.keys(formDataValues.value[item]).map((key) => {
						if (
							formDataValues.value[item][key] !== undefined &&
							formDataValues.value[item][key]['value'] !== null &&
							formDataValues.value[item][key]['value'] !== undefined
						) {
							result[key] = formDataValues.value[item][key]['value']
						} else if (formDataValues.value[item][key] === undefined) {
							result[key] = null
						} else {
							result[key] = formDataValues.value[item][key]
						}
					})
					output[item] = result
				} else {
					output[item] = formDataValues.value[item]
				}
			})

			return output
		}

		const goNext = async () => {
			if (!formData.isValid) {
				await formData.validate()
			}

			if (formData.isValid) {
				if (currentStep.value.step === 4) {
					open.value = true
					try {
						const data = mapFormValues()

						// remove image property from data.uploads
						const uploads = data.uploads.map((item) => {
							const obj = {}
							Object.keys(item).map((key) => {
								if (key !== 'image') {
									obj[key] = item[key]
								}
							})
							return obj
						})

						data.uploads = uploads

						const response = await axios.post(api_url, data)

						if (response.status === 200) {
							store.dispatch('steps/goNext', formData.formValues)
						}
					} catch (err) {
						if (err.response) {
							// client received an error response (5xx, 4xx)
							console.log('Server Error:', err)
						} else if (err.request) {
							// client never received a response, or request never left
							console.log('Network Error:', err)
						} else {
							console.log('Client Error:', err)
						}
					} finally {
						open.value = false
					}
				} else {
					store.dispatch('steps/goNext', formData.formValues)
				}
			} else {
				return
			}
		}

		const goPrevious = () => {
			store.dispatch('steps/goPrevious')
		}

		let formData = reactive({})

		const updateFormData = (event) => {
			formData = event.value
		}

		let open = ref(false)

		return {
			goNext,
			goPrevious,
			transitionName,
			currentStep,
			steps,
			isFirstStep,
			updateFormData,
			formData,
			open,
		}
	},
}
</script>
